'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { RiMapPin2Line } from 'react-icons/ri';
import { format } from 'date-fns';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CategoryBadge } from './card/category-badge';
import { htmlToPlainText } from '@eliadmin/core/src/utils/html-utils';

export interface CompanyCardSmallProps {
  company: any;
}

export const CompanyCardSmall = ({ company }: CompanyCardSmallProps) => {
  const { t } = useTranslations();

  // Fallback image if none provided
  const imageUrl = company.image?.url || '/images/placeholder.png';
  const dualEducationTag = company.tags.find((tag: any) => tag.id === 4);
  const isDualEducation = Boolean(dualEducationTag);
  const filteredTags = company.tags.filter((tag: any) => tag.id !== 4);

  return (
    <Link
      prefetch={false}
      href={company.slug}
      className="group flex h-[362px] w-full flex-col justify-between overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition hover:shadow-md"
    >
      <div className="relative h-[168px] min-h-[168px] w-full">
        <Image src={imageUrl} alt={resolveImageAlt(company?.image, company?.name)} fill className="object-contain" sizes="100vw" />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="line-clamp-2 text-sm font-bold text-foreground">{company.name}</div>
        {company.city && (
          <div className="flex items-center gap-1 text-xs text-primary">
            <RiMapPin2Line size={16} />
            <span>{company.city}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-1">
          {company.categories.length > 0 && (
            <>
              <CategoryBadge
                key={company.categories[0].id}
                category={company.categories[0]}
                asLink={false}
              />
              {company.categories.length > 1 && (
                <span className="flex items-center rounded-[8px] bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  +{company.categories.length - 1}
                </span>
              )}
            </>
          )}
        </div>
        {filteredTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {filteredTags.map((tag: any) => (
              <span
                key={tag.id}
                className={cn(
                  'rounded-full px-2 py-0.5 text-[10px] font-medium',
                  tag.color ? `bg-[${tag.color}] text-white` : 'bg-primary text-white',
                )}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
        {isDualEducation && company.slogan && (
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-semibold text-primary">Poskytuje prax v</h3>
            <p className="text-xs font-medium text-black">{htmlToPlainText(company.slogan)}</p>
          </div>
        )}
        <div className="mt-auto flex items-center justify-between text-[10px] text-muted-foreground">
          <div>
            {t('Pridané')}:&nbsp;
            {company.created_at ? format(new Date(company.created_at), 'dd.MM.yyyy') : '---'}
          </div>
          {dualEducationTag && (
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-medium',
                dualEducationTag.color
                  ? `bg-[${dualEducationTag.color}] text-white`
                  : 'bg-primary text-white',
              )}
            >
              {dualEducationTag.name}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
